"use client";

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Icon } from "@/components/ui/Icon";

/* A horizontally scrolling rail with arrow affordances that disable
   themselves at each end — used by every "View All" section in the kit. */
export function useRail() {
  const ref = useRef<HTMLDivElement>(null);
  /* `pages` is 0 until the rail is measured, so page dots render nothing on
     the server and the real count appears after mount. */
  const [state, setState] = useState({ start: true, end: false, page: 0, pages: 0 });
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReducedMotion(query.matches);

    sync();
    query.addEventListener("change", sync);

    return () => query.removeEventListener("change", sync);
  }, []);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const stops = stopsOf(el);
    setState({
      start: el.scrollLeft <= 2,
      end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 2,
      page: stopIndexAt(el, stops),
      pages: stops.length,
    });
  }, []);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  const scrollBy = (dir: -1 | 1) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({
      left: dir * Math.round(el.clientWidth * 0.82),
      behavior: reducedMotion ? "auto" : "smooth",
    });
  };

  const scrollToPage = (i: number) => {
    const el = ref.current;
    if (!el) return;
    const stops = stopsOf(el);
    const target = stops[Math.max(0, Math.min(i, stops.length - 1))];
    el.scrollTo({ left: target, behavior: reducedMotion ? "auto" : "smooth" });
  };

  /* One page per click: the neighbouring stop, not "index ± 1 times the
     card pitch" — near the end a card's natural offset lies beyond the
     maximum scroll, where a computed target either overshoots into the
     clamp or clamps back to where the rail already is. */
  const scrollByPage = (dir: -1 | 1) => {
    const el = ref.current;
    if (!el) return;
    const stops = stopsOf(el);
    const target = stops[stopIndexAt(el, stops) + dir];
    if (target === undefined) return;
    el.scrollTo({ left: target, behavior: reducedMotion ? "auto" : "smooth" });
  };

  return { ref, state, measure, scrollBy, scrollByPage, scrollToPage };
}

/* The positions the rail can actually rest at: each card's offset clamped
   into the scrollable range, duplicates collapsed. With cards wider than
   the leftover range the tail cards share the end position, so a rail of
   three cards showing two has two stops, not three — and the dots, the
   arrows and the active-page indicator all count these, never the cards. */
function stopsOf(el: HTMLDivElement): number[] {
  const max = el.scrollWidth - el.clientWidth;
  if (max <= 2 || el.children.length === 0) return [0];

  const items = el.children as HTMLCollectionOf<HTMLElement>;
  const first = items[0].offsetLeft;
  const stops: number[] = [];
  for (const item of Array.from(items)) {
    const stop = Math.min(item.offsetLeft - first, max);
    if (stops.length === 0 || stop - stops[stops.length - 1] > 2) stops.push(stop);
  }
  return stops;
}

function stopIndexAt(el: HTMLDivElement, stops: number[]): number {
  const here = el.scrollLeft;
  let best = 0;
  stops.forEach((stop, i) => {
    if (Math.abs(stop - here) < Math.abs(stops[best] - here)) best = i;
  });
  return best;
}

/* `md` is the square button beside a panel's title; `sm` the round chevron
   pill the kit's uncontained section headers use. */
export type RailArrowSize = "md" | "sm";

export function RailArrows({
  state,
  scrollBy,
  size = "md",
  className,
}: {
  state: { start: boolean; end: boolean };
  scrollBy: (dir: -1 | 1) => void;
  size?: RailArrowSize;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center", size === "sm" ? "gap-[4px]" : "gap-[8px]", className)}>
      <RailArrow dir={-1} disabled={state.start} onClick={() => scrollBy(-1)} size={size} />
      <RailArrow dir={1} disabled={state.end} onClick={() => scrollBy(1)} size={size} />
    </div>
  );
}

function RailArrow({
  dir,
  disabled,
  onClick,
  size,
}: {
  dir: -1 | 1;
  disabled: boolean;
  onClick: () => void;
  size: RailArrowSize;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={dir === -1 ? "Scroll left" : "Scroll right"}
      className={cn(
        "grid shrink-0 place-items-center bg-elevation-2 transition-colors duration-200",
        size === "sm" ? "h-[32px] w-[32px] rounded-rounded" : "h-[38px] w-[38px] rounded-lg",
        disabled ? "cursor-not-allowed text-fg-disabled" : "text-fg hover:bg-elevation-3",
      )}
    >
      {size === "sm" ? (
        <Icon name={dir === -1 ? "chevron-left" : "chevron-right"} size={14} />
      ) : (
        <Icon name={dir === -1 ? "arrow-left" : "arrow-right"} size={20} />
      )}
    </button>
  );
}

export function RailTrack({
  railRef,
  label,
  onScroll,
  children,
  className,
  padded = true,
}: {
  railRef: React.RefObject<HTMLDivElement | null>;
  label?: string;
  onScroll: () => void;
  children: ReactNode;
  className?: string;
  /* Inside a panel the track keeps the panel's inset when it snaps; on the
     canvas there is no inset to keep. */
  padded?: boolean;
}) {
  return (
    <div
      ref={railRef}
      role={label ? "region" : undefined}
      aria-label={label}
      tabIndex={label ? 0 : undefined}
      onScroll={onScroll}
      className={cn(
        "scrollbar-none flex snap-x snap-mandatory gap-[12px] overflow-x-auto overflow-y-hidden scroll-smooth",
        padded && "scroll-p-[16px]",
        className,
      )}
    >
      {children}
    </div>
  );
}
